import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Clapperboard, Wand2, Zap } from "lucide-react";
import { VideoCard } from "../components/VideoCard";
import { FavoritesShelf } from "../components/FavoritesShelf";
import { kidApi } from "../lib/api";
import { CATEGORIES } from "../lib/constants";
import { useAuth } from "../context/AuthContext";

export default function KidHome() {
  const { kid } = useAuth();
  const [cat, setCat] = useState("");
  const [q, setQ] = useState("");
  const [videos, setVideos] = useState(null);
  const allowed = CATEGORIES.filter((c) => kid.settings?.allowed_categories?.includes(c.id));

  useEffect(() => {
    const t = setTimeout(() => {
      kidApi.get("/kid/feed", { params: { category: cat || undefined, q: q || undefined } }).then((r) => setVideos(r.data)).catch(() => setVideos([]));
    }, 250);
    return () => clearTimeout(t);
  }, [cat, q]);

  return (
    <div>
      <section className="grid grid-cols-1 md:grid-cols-12 gap-4 sm:gap-6 mb-6 sm:mb-10">
        <div className="md:col-span-8 zn-card zn-pop p-6 sm:p-10 bg-[#FF6B4A] text-white relative overflow-hidden">
          <Wand2 className="absolute -right-6 -bottom-8 opacity-20 rotate-12 w-28 h-28 sm:w-40 sm:h-40" />
          <p className="font-display text-base sm:text-lg opacity-90">Hey {kid.name}!</p>
          <h1 className="text-3xl sm:text-5xl font-bold mb-4 sm:mb-6 leading-tight">What will you <br />create today?</h1>
          <div className="flex flex-wrap gap-3">
            <Link to="/kid/studio" data-testid="home-create-btn" className="zn-btn bg-[#FFD23F] text-[#1B1A2E] !py-2.5 sm:!py-3"><Clapperboard size={18} /> Make a video</Link>
            <Link to="/kid/shorts" data-testid="home-shorts-btn" className="zn-btn bg-white text-[#1B1A2E] !py-2.5 sm:!py-3"><Zap size={18} /> Watch Shorts</Link>
          </div>
        </div>
        <div className="hidden md:flex md:col-span-4 zn-card zn-pop p-6 bg-[#3DCCC7] flex-col justify-center" style={{ animationDelay: "80ms" }}>
          <p className="font-display text-xl font-semibold mb-1">Be kind, be you</p>
          <p className="text-[#1B1A2E]/80">Cheer for friends, keep your real name and school a secret, and have fun!</p>
        </div>
      </section>

      <FavoritesShelf />
      <div className="sticky top-[calc(4rem+env(safe-area-inset-top))] sm:static z-20 -mx-4 px-4 py-3 sm:p-0 sm:mx-0 bg-[#FFF7EC]/95 backdrop-blur-md sm:bg-transparent sm:backdrop-blur-none flex flex-col lg:flex-row gap-3 sm:gap-4 lg:items-center mb-4 sm:mb-8">
        <div className="relative lg:w-80">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2" size={18} />
          <input data-testid="feed-search-input" type="search" enterKeyHint="search" className="zn-input !pl-11 !py-2.5 sm:!py-3" placeholder="Search videos" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <div className="flex gap-2 overflow-x-auto no-scrollbar -mx-4 px-4 lg:mx-0 lg:px-0 pb-1 snap-x scroll-px-4 lg:scroll-px-0">
          <button data-testid="feed-filter-all" onClick={() => setCat("")} className={`zn-chip snap-start shrink-0 ${!cat ? "bg-[#1B1A2E] text-white" : "bg-white"}`}>All</button>
          {allowed.map(({ id, label, icon: Icon, color }) => (
            <button key={id} data-testid={`feed-filter-${id}`} onClick={() => setCat(id)} className="zn-chip whitespace-nowrap snap-start shrink-0" style={{ background: cat === id ? color : "#fff" }}>
              <Icon size={15} /> {label}
            </button>
          ))}
        </div>
      </div>

      {videos === null ? (
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-8" data-testid="feed-loading">
          {[0, 1, 2, 3].map((i) => <div key={i} className="aspect-[4/5] sm:aspect-video rounded-2xl sm:rounded-3xl bg-[#1B1A2E]/10 animate-pulse" />)}
        </div>
      ) : videos.length === 0 ? (
        <div data-testid="feed-empty" className="zn-card p-8 sm:p-10 text-center bg-white">
          <h3 className="text-2xl font-bold mb-2">No videos here yet</h3>
          <p className="text-[#1B1A2E]/70 mb-6">Be the first to share something awesome!</p>
          <Link to="/kid/studio" data-testid="feed-empty-create-btn" className="zn-btn bg-[#FFD23F]">Open the studio</Link>
        </div>
      ) : (
        <div data-testid="video-feed" className="grid grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-8">
          {videos.map((v, i) => <VideoCard key={v.id} video={v} index={i} />)}
        </div>
      )}
    </div>
  );
}
